'use client';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-operator-void text-gray-300 px-4 py-8 sm:py-12">
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase text-white mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500">Last updated: February 16, 2026</p>
        </div>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">1. Introduction</h2>
          <p className="text-sm leading-relaxed">
            OPMODE (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is a tactical fitness training application. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the OPMODE mobile application and web app (collectively, the &quot;Service&quot;). By using the Service, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">2. Information We Collect</h2>

          <h3 className="text-base font-semibold text-white">2.1 Account Information</h3>
          <p className="text-sm leading-relaxed">
            When you create an account, we collect your email address and display name via Firebase Authentication (Google sign-in or email/password). We may also store a callsign, age, gender, height, weight, and experience level that you provide during onboarding.
          </p>

          <h3 className="text-base font-semibold text-white">2.2 Workout &amp; Fitness Data</h3>
          <p className="text-sm leading-relaxed">
            We collect workout session data (exercises, sets, reps, weights), recovery logs, fitness assessment results, and one-rep max records. This data is stored locally on your device using Dexie (IndexedDB) and synced to Google Cloud Firestore for cross-device access.
          </p>

          <h3 className="text-base font-semibold text-white">2.3 Health &amp; Wearable Data</h3>
          <p className="text-sm leading-relaxed">
            If you connect a health or wearable service — including <strong>Android Health Connect</strong>, Apple Health, or Google Fit — OPMODE reads the following data types, each used to power a specific in-app feature:
          </p>
          <ul className="text-sm leading-relaxed list-disc pl-5 space-y-1 mt-2">
            <li><strong>Steps, active calories, active minutes</strong> — daily activity tracking, NEAT (non-exercise activity) scoring, and your activity zone.</li>
            <li><strong>Heart rate</strong> — your daily average heart rate, weekly Zone 2 aerobic-base tracking, and near-real-time heart rate shown during logged workouts.</li>
            <li><strong>Resting heart rate</strong> — a recovery and cardiovascular-load signal shown on the Recovery screen and used in your daily readiness score and cardiac-age estimate.</li>
            <li><strong>Heart rate variability (HRV)</strong> — autonomic recovery status, the primary input to your daily readiness score, also shown on the Recovery screen.</li>
            <li><strong>Sleep</strong> — sleep duration used to calculate readiness, sleep debt, and adaptive training recommendations; shown on the Recovery screen.</li>
            <li><strong>Body weight and body fat percentage</strong> — body-composition tracking (weight, lean mass, fat mass trends).</li>
          </ul>
          <p className="text-sm leading-relaxed mt-2">
            This data is used solely to provide the recovery, readiness, activity, and body-composition features described above. It is stored in Google Cloud Firestore under your user account and is never sold or shared with advertisers. You can disconnect a health service at any time from your device's Health Connect / Apple Health settings or the app's Settings screen, and you can request deletion of your stored health data by contacting support.
          </p>

          <h3 className="text-base font-semibold text-white">2.4 Subscription Data</h3>
          <p className="text-sm leading-relaxed">
            Subscription purchases are processed by Google Play or Apple App Store. We use RevenueCat to manage entitlements. We do not store your payment card details. RevenueCat receives your anonymous app user ID and purchase receipt to verify your subscription status.
          </p>

          <h3 className="text-base font-semibold text-white">2.5 Analytics &amp; Diagnostics</h3>
          <p className="text-sm leading-relaxed">
            We use Firebase Analytics to collect anonymous usage events (e.g., session completion, screen views) to improve the app experience. We also collect web performance metrics (LCP, INP, CLS). Bug reports submitted through the app include device info, app version, and error logs.
          </p>

          <h3 className="text-base font-semibold text-white">2.6 Local Machine Learning</h3>
          <p className="text-sm leading-relaxed">
            OPMODE uses TensorFlow.js to run machine learning models entirely on your device for load recommendations and progression predictions. No training data or model outputs are sent to external servers.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">3. How We Use Your Information</h2>
          <ul className="text-sm leading-relaxed list-disc list-inside space-y-1">
            <li>Provide, maintain, and improve the Service</li>
            <li>Sync your workout data across devices</li>
            <li>Generate personalized load recommendations and progression analytics</li>
            <li>Compute operational readiness and recovery scores</li>
            <li>Manage your subscription and verify entitlements</li>
            <li>Send push notifications (with your permission) for workout reminders</li>
            <li>Analyze aggregate usage patterns to improve the product</li>
            <li>Respond to bug reports and customer support requests</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">4. Data Storage &amp; Security</h2>
          <p className="text-sm leading-relaxed">
            Your data is stored in Google Cloud Firestore (US region) and locally on your device via IndexedDB. All data in transit is encrypted via HTTPS/TLS. Firestore security rules ensure that users can only read and write their own data. Firebase Authentication tokens are used to verify identity on every request.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">5. Data Sharing</h2>
          <p className="text-sm leading-relaxed">
            We do not sell your personal data. We share data only with the following service providers who process it on our behalf:
          </p>
          <ul className="text-sm leading-relaxed list-disc list-inside space-y-1">
            <li><strong>Google Firebase</strong> &mdash; Authentication, database, hosting, analytics, cloud functions</li>
            <li><strong>RevenueCat</strong> &mdash; Subscription management and entitlement verification</li>
            <li><strong>Google Fit</strong> &mdash; Health data sync (only if you connect your account)</li>
          </ul>
          <p className="text-sm leading-relaxed">
            We may also disclose information if required by law or to protect the rights and safety of our users.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">6. Data Retention &amp; Deletion</h2>
          <p className="text-sm leading-relaxed">
            We retain your data for as long as your account is active. You can export your data at any time from Settings. You can delete your account and all associated data from Settings &gt; Account &gt; Delete Account. Upon deletion, all your data is permanently removed from our servers within 30 days. Local data on your device is cleared immediately.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">7. Your Rights</h2>
          <p className="text-sm leading-relaxed">
            Depending on your jurisdiction, you may have the right to:
          </p>
          <ul className="text-sm leading-relaxed list-disc list-inside space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Export your data in a portable format</li>
            <li>Withdraw consent for optional data processing (e.g., analytics, notifications)</li>
          </ul>
          <p className="text-sm leading-relaxed">
            To exercise any of these rights, use the in-app settings or contact us at the email below.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">8. Children&apos;s Privacy</h2>
          <p className="text-sm leading-relaxed">
            OPMODE is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected data from a child, please contact us immediately.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">9. Changes to This Policy</h2>
          <p className="text-sm leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the &quot;Last updated&quot; date. Your continued use of the Service after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-heading font-bold text-operator-gold uppercase">10. Contact Us</h2>
          <p className="text-sm leading-relaxed">
            If you have questions about this Privacy Policy, please contact us at:
          </p>
          <p className="text-sm font-mono text-white">support@tactivlabs.com</p>
        </section>

        <div className="border-t border-operator-steel/40 pt-6 text-xs text-gray-500 text-center">
          OPMODE &mdash; Tactical Fitness Training
        </div>
      </div>
    </div>
  );
}
